import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import Tag from './Tag';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

describe('Tag', () => {
  it('renders children', () => {
    render(<Tag>Active</Tag>);
    expect(screen.getByText('Active')).toBeInTheDocument();
  });

  it('renders as a span', () => {
    const { container } = render(<Tag>Status</Tag>);
    expect(container.firstChild?.nodeName).toBe('SPAN');
  });

  it('applies custom className', () => {
    const { container } = render(<Tag className="custom">Tag</Tag>);
    expect(container.firstChild).toHaveClass('custom');
  });

  describe('variants', () => {
    const variants = ['default', 'success', 'info', 'warning', 'error'] as const;

    variants.forEach(variant => {
      it(`renders ${variant} variant`, () => {
        const { container } = render(<Tag variant={variant}>Tag</Tag>);
        expect(container.querySelector(`.${variant}`)).toBeInTheDocument();
      });
    });

    it('defaults to default variant', () => {
      const { container } = render(<Tag>Tag</Tag>);
      expect(container.querySelector('.default')).toBeInTheDocument();
    });
  });

  describe('icons', () => {
    it('renders left icon', () => {
      const { container } = render(<Tag iconLeft="check">Done</Tag>);
      expect(container.querySelector('[data-icon="check"]')).toBeInTheDocument();
    });

    it('renders right icon', () => {
      const { container } = render(<Tag iconRight="close">Remove</Tag>);
      expect(container.querySelector('[data-icon="close"]')).toBeInTheDocument();
    });

    it('renders both icons', () => {
      const { container } = render(<Tag iconLeft="check" iconRight="close">Tag</Tag>);
      const icons = container.querySelectorAll('[data-icon]');
      expect(icons).toHaveLength(2);
    });

    it('marks decorative icons as aria-hidden', () => {
      const { container } = render(<Tag iconLeft="check">Done</Tag>);
      expect(container.querySelector('[data-icon="check"]')).toHaveAttribute('aria-hidden', 'true');
    });
  });

  describe('clickable icons', () => {
    it('renders left icon as button when onClick is provided', () => {
      const onClick = jest.fn();
      render(<Tag iconLeft="close" onIconLeftClick={onClick} iconLeftLabel="Remove tag">Tag</Tag>);
      const button = screen.getByLabelText('Remove tag');
      expect(button.tagName).toBe('BUTTON');
    });

    it('renders right icon as button when onClick is provided', () => {
      const onClick = jest.fn();
      render(<Tag iconRight="close" onIconRightClick={onClick} iconRightLabel="Dismiss">Tag</Tag>);
      const button = screen.getByLabelText('Dismiss');
      expect(button.tagName).toBe('BUTTON');
    });

    it('calls onIconLeftClick when clicked', () => {
      const onClick = jest.fn();
      render(<Tag iconLeft="close" onIconLeftClick={onClick} iconLeftLabel="Remove">Tag</Tag>);
      fireEvent.click(screen.getByLabelText('Remove'));
      expect(onClick).toHaveBeenCalledTimes(1);
    });

    it('calls onIconRightClick when clicked', () => {
      const onClick = jest.fn();
      render(<Tag iconRight="close" onIconRightClick={onClick} iconRightLabel="Dismiss">Tag</Tag>);
      fireEvent.click(screen.getByLabelText('Dismiss'));
      expect(onClick).toHaveBeenCalledTimes(1);
    });

    it('has type="button" on icon buttons', () => {
      render(<Tag iconLeft="close" onIconLeftClick={jest.fn()} iconLeftLabel="Remove">Tag</Tag>);
      expect(screen.getByLabelText('Remove')).toHaveAttribute('type', 'button');
    });

    it('marks icon inside button as aria-hidden', () => {
      render(<Tag iconLeft="close" onIconLeftClick={jest.fn()} iconLeftLabel="Remove">Tag</Tag>);
      const button = screen.getByLabelText('Remove');
      expect(button.querySelector('[data-icon="close"]')).toHaveAttribute('aria-hidden', 'true');
    });
  });
});
